import {Color} from '@benchling/nucleus-ui';
import {UnstyledButton, Modal, Flex, Text, Button, Box, Group, Loader} from '@mantine/core';
import {useDisclosure} from '@mantine/hooks';
import {css, StyleSheet} from 'aphrodite';
import DOMPurify from 'dompurify';
import reactUpdate from 'immutability-helper';
import React, {memo, useEffect, useState} from 'react';
import {v4 as uuidv4} from 'uuid';

import {EMPTY_MRV} from 'chemistry/ChemistryConstants';
import {generateReactionImage, parseReactionComponents} from 'chemistry/ChemistryDAO';
import {
  ChemicalStructureDrawer,
  ChemicalStructureDrawerMode,
  ChemicalStructureDrawerValue,
} from 'chemistry/marvinjs-wrappers/ChemicalStructureDrawer';
import {MarvinChemicalFormat} from 'chemistry/marvinjs-wrappers/MarvinJSConstants';
import SignedExtFileURLProxy from 'external-files/SignedExtFileURLProxy';
import {
  ChemistryReactionRole,
  CHEMISTRY_REACTION_ROLE_TO_DISPLAY_NAME,
} from 'notebook/integrations/chemistry/ChemistryStructuredTableConstants';
import {
  StoichiometryStructuredTableRowMetadata,
  StoichiometryStructuredTable,
  StoichiometryTableCustomSlotProps,
} from 'notebook/integrations/chemistry/StoichiometryStructuredTableAdapter';
import {StructuredTableColumn, StructuredTableRow} from 'notebook/structured-tables/StructuredTableConstants';
import getBlankRow from 'notebook/structured-tables/util/getBlankRow';
import {BaseTypedValueType} from 'notebook/tables/TableConstants';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import useAsyncState, {AsyncStatus} from 'ui-library/hooks/useAsyncState';
import useResizeSVG from 'ui-library/hooks/useResizeSVG';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import ModalFooter from 'ui-library/modals/ModalFooter';
import {withLoadingScreen} from 'util/Promises';

interface ReactionSketcherModalProps {
  onClose: () => void;
  onSubmit: (newMarvinStructure: string) => Promise<void>;
  initialMarvinStructure: ChemicalStructureDrawerValue;
}

function ReactionSketcherModal({
  onClose,
  onSubmit,
  initialMarvinStructure,
}: ReactionSketcherModalProps): JSX.Element {
  const [structureContent, setStructureContent] =
    useState<ChemicalStructureDrawerValue>(initialMarvinStructure);
  const [hasReactionContents, setHasReactionContents] = useState(false);

  return (
    <Modal
      title="Reaction Sketcher"
      opened={true}
      onClose={onClose}
      size="xl"
      styles={{
        content: {
          height: '90vh',
          display: 'flex',
          flexDirection: 'column',
        },
        body: {
          flex: 1,
        },
      }}
    >
      <Flex direction="column" h="100%">
        <ChemicalStructureDrawer
          mode={ChemicalStructureDrawerMode.REACTION}
          onMoleculeChange={setStructureContent}
          setHasReactionContents={setHasReactionContents}
          initialValue={initialMarvinStructure}
          inputFormat={MarvinChemicalFormat.MRV}
          outputFormat={MarvinChemicalFormat.MRV}
        />
        <ModalFooter>
          <Group position="right">
            <Button type="button" onClick={onClose} variant="secondary">
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!hasReactionContents}
              onClick={() => {
                onSubmit(structureContent!);
              }}
            >
              Save
            </Button>
          </Group>
        </ModalFooter>
      </Flex>
    </Modal>
  );
}

// Note: could port this over to use `chemicalStructureFromFile`
async function fetchMarvinContent(marvinFileUrl: string | null | undefined): Promise<string | null> {
  if (marvinFileUrl != null) {
    const response = await fetch(marvinFileUrl);
    if (response.ok) {
      return await response.text();
    } else {
      throw new Error('Failed to fetch Marvin file content');
    }
  }
  return null;
}

function ReactionSchemeContent({
  marvinFileUrl,
  setMarvinStructureContent,
}: {
  marvinFileUrl: string | null | undefined;
  setMarvinStructureContent: (marvinStructureContent: string | null) => void;
}): JSX.Element {
  const reactionSVGRef = useResizeSVG<HTMLDivElement>(css(styles.reactionSVG));
  const {currentState: marvinContentAsyncState, makeCall: fetchMarvinContentAsync} = useAsyncState<
    [string | null | undefined],
    string | null
  >({makeCall: fetchMarvinContent});
  const {currentState: reactionBase64SVGAsyncState, makeCall: generateReactionImageAsync} = useAsyncState({
    makeCall: generateReactionImage,
  });

  useEffect(() => {
    fetchMarvinContentAsync(marvinFileUrl);
  }, [marvinFileUrl, fetchMarvinContentAsync]);

  // Intentionally avoid using `getMostRecentData` since we do not want stale data.
  const currentMarvinStructureContent =
    marvinContentAsyncState.status === AsyncStatus.SUCCESS && marvinContentAsyncState.data != null
      ? marvinContentAsyncState.data
      : null;

  useEffect(() => {
    setMarvinStructureContent(currentMarvinStructureContent);
    if (currentMarvinStructureContent != null) {
      generateReactionImageAsync(currentMarvinStructureContent);
    }
  }, [generateReactionImageAsync, currentMarvinStructureContent, setMarvinStructureContent]);

  if (
    marvinFileUrl === undefined ||
    marvinContentAsyncState.status === AsyncStatus.LOADING ||
    reactionBase64SVGAsyncState.status === AsyncStatus.LOADING
  ) {
    // TODO: disable cursor while loading
    return <Loader size="xl" />;
  } else if (
    marvinContentAsyncState.status === AsyncStatus.SUCCESS &&
    marvinContentAsyncState.data != null &&
    reactionBase64SVGAsyncState.status === AsyncStatus.SUCCESS
  ) {
    return (
      <Box
        ref={reactionSVGRef}
        role="img"
        aria-label="Reaction diagram"
        w="100%"
        h="100%"
        p="12px"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
        // oxlint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(atob(reactionBase64SVGAsyncState.data.base64SVG)),
        }}
      />
    );
  } else {
    // TODO: Use design-approved error state UI
    return <InlineBanner bannerType={InlineBannerType.ERROR} text="Failed to load reaction scheme" />;
  }
}

async function handleReactionSketcherSubmit(
  mrvStructure: string,
  structuredTable: StoichiometryStructuredTable,
  updateHandler: (structuredTable: StoichiometryStructuredTable) => void
): Promise<void> {
  // Parse the reaction into its components.
  const {externalFile, reactants, products} = await parseReactionComponents(mrvStructure);

  // TODO: use constants or some more stable approach to identify columns.
  const nameColumnIndex = structuredTable.columns.findIndex((col) => col.name === 'Compound');
  const roleColumnIndex = structuredTable.columns.findIndex((col) => col.name === 'Role');

  if (nameColumnIndex === -1 || roleColumnIndex === -1) {
    throw new Error('Could not find required columns');
  }

  const reactantRows = createRowsForReactionComponent(
    reactants,
    ChemistryReactionRole.REACTANT,
    nameColumnIndex,
    roleColumnIndex,
    structuredTable.columns,
    0
  );

  const productRows = createRowsForReactionComponent(
    products,
    ChemistryReactionRole.PRODUCT,
    nameColumnIndex,
    roleColumnIndex,
    structuredTable.columns,
    reactantRows.length
  );

  // Overwrite the table rows and store the external file reference in metadata
  updateHandler({
    ...structuredTable,
    metadata: {
      ...structuredTable.metadata,
      reactionMrvExternalFile: {
        id: externalFile.id,
        url: externalFile.url,
      },
    },
    rows: [...reactantRows, ...productRows],
  });
}

function createRowsForReactionComponent(
  components: Array<{moleculeId: string; name: string}>,
  role: ChemistryReactionRole,
  nameColumnIndex: number,
  roleColumnIndex: number,
  columns: Array<StructuredTableColumn>,
  startRowIndex: number
): Array<StructuredTableRow<StoichiometryStructuredTableRowMetadata>> {
  const rows: Array<StructuredTableRow<StoichiometryStructuredTableRowMetadata>> = [];
  let rowIndex = startRowIndex;

  for (const component of components) {
    const blankCells = getBlankRow(rowIndex, columns, null);
    const cells = reactUpdate(blankCells, {
      [nameColumnIndex]: {
        text: {$set: component.name},
        typedValue: {
          $set: {
            type: BaseTypedValueType.BIOENTITY,
            bioentitySummary: {id: component.moleculeId, name: component.name},
          },
        },
      },
      [roleColumnIndex]: {
        text: {$set: CHEMISTRY_REACTION_ROLE_TO_DISPLAY_NAME[role]},
        typedValue: {
          $set: {type: BaseTypedValueType.STRING, rawValue: CHEMISTRY_REACTION_ROLE_TO_DISPLAY_NAME[role]},
        },
      },
    });

    rows.push({
      cells,
      rowId: uuidv4(),
      isFinalized: false,
      metadata: {},
      lastSyncedUpstreamValues: null,
    });
    rowIndex++;
  }

  return rows;
}

/** A chemical sketcher rendered inside a Stoichiometry Structured Table. It can be used to populate the
 * table with reactants and products.
 */
function StoichiometryTableEmbeddedChemicalSketcher(props: StoichiometryTableCustomSlotProps): JSX.Element {
  const [isSketcherModalOpen, {open: openSketcherModal, close: closeSketcherModal}] = useDisclosure(false);
  const [marvinStructure, setMarvinStructure] = useState<ChemicalStructureDrawerValue>(EMPTY_MRV);
  const reactionMrvExternalFile = props.structuredTable.metadata.reactionMrvExternalFile;

  return (
    <>
      {isSketcherModalOpen && (
        <ReactionSketcherModal
          initialMarvinStructure={marvinStructure}
          onClose={closeSketcherModal}
          onSubmit={async (newMarvinStructure: string): Promise<void> => {
            closeSketcherModal();
            setMarvinStructure(newMarvinStructure);
            await withLoadingScreen(
              handleReactionSketcherSubmit(newMarvinStructure, props.structuredTable, props.updateHandler)
            );
          }}
        />
      )}

      <UnstyledButton
        role="button"
        tabIndex={0}
        aria-label="Open reaction sketcher"
        // Should this button be disabled while the reaction SVG is loading?
        onClick={openSketcherModal}
        h="150px"
        w="100%"
        style={{
          border: `1px solid ${Color.GRAY_250}`,
          borderTop: 'none',
        }}
      >
        <Flex direction="column" justify="center" align="center" h="100%">
          {reactionMrvExternalFile != null ? (
            <SignedExtFileURLProxy
              url={reactionMrvExternalFile.url}
              externalFileId={reactionMrvExternalFile.id}
              // The sketcher is always expected to be rendered within an Entry so fileId should not be null
              owningEntityApiId={props.structuredTable.fileId!}
              renderWithSignedURL={(signedMarvinFileUrl: string | null | undefined): JSX.Element => (
                <ReactionSchemeContent
                  marvinFileUrl={signedMarvinFileUrl}
                  setMarvinStructureContent={setMarvinStructure}
                />
              )}
            />
          ) : (
            <>
              <BenchlingIcon
                icon="molecule"
                style={{width: '24px', height: '24px', color: Color.GRAY_600}}
                altText="Molecule"
              />
              <Text size="xl" color="dimmed">
                Click to start sketching
              </Text>
            </>
          )}
        </Flex>
      </UnstyledButton>
    </>
  );
}

export default memo(StoichiometryTableEmbeddedChemicalSketcher);

const styles = StyleSheet.create({
  // Ensure SVG scales proportionally within the container. useResizeSVG expects a single className arg so
  // define this style but prefer Mantine props otherwise.
  reactionSVG: {
    maxWidth: '100%',
    maxHeight: '100%',
  },
});
